import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronRight } from "lucide-react";

interface StatusChangeFormProps {
  statusName: string;
  isProcessing: boolean;
  requiresReason?: boolean;
  availableReasons?: { name: string }[];
  onSubmit: (comments: string, reason?: string) => Promise<void>;
  onCancel: () => void;
}

export const StatusChangeForm: React.FC<StatusChangeFormProps> = ({
  statusName,
  isProcessing,
  requiresReason = false,
  availableReasons = [],
  onSubmit,
  onCancel
}) => {
  const [comments, setComments] = useState("");
  const [selectedReason, setSelectedReason] = useState<string>("");

  const handleSubmit = async () => {
    await onSubmit(comments, selectedReason || undefined);
    setComments("");
    setSelectedReason("");
  };

  const handleCancel = () => {
    setComments("");
    setSelectedReason("");
    onCancel();
  };

  const isSubmitDisabled = isProcessing || (requiresReason && !selectedReason);

  return (
    <div className="overflow-hidden animate-in slide-in-from-top-2 fade-in duration-300">
      <div className="px-4 pb-4 border-t border-blue-200">
        <div className="space-y-4 pt-4">
          {requiresReason && availableReasons.length > 0 && (
            <div>
              <label htmlFor={`reason-${statusName}`} className="block text-sm font-medium text-blue-900 mb-2">
                Reason (required):
              </label>
              <Select value={selectedReason} onValueChange={setSelectedReason} disabled={isProcessing}>
                <SelectTrigger className="w-full border-blue-200 focus:border-blue-400 focus:ring-blue-400">
                  <SelectValue placeholder="Select a reason..." />
                </SelectTrigger>
                <SelectContent>
                  {availableReasons.map((reason) => (
                    <SelectItem key={reason.name} value={reason.name}>
                      {reason.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <label htmlFor={`comments-${statusName}`} className="block text-sm font-medium text-blue-900 mb-2">
              Comments (optional):
            </label>
            <Input
              id={`comments-${statusName}`}
              type="text"
              placeholder="Add a comment about this status change..."
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              className="w-full border-blue-200 focus:border-blue-400 focus:ring-blue-400"
              disabled={isProcessing}
            />
          </div>

          <div className="flex gap-3">
            <Button
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
              variant="thalo"
              className="flex items-center justify-center gap-2 flex-1"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Updating...</span>
                </>
              ) : (
                <>
                  <span>Confirm</span>
                  <ChevronRight className="h-4 w-4"/>
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isProcessing}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};